// autopilot-archive-command.ts
//
// Phase 02 (D5, R-13/R-14): `avatar autopilot archive [--mission <id>]
// [--repo <path>] [--force] [--json]` — moves a mission's directory out of
// `.avatar/missions/` (so `resolveMission` stops listing it) and removes its
// worktree. Registration pattern mirrors autopilot-unlock-command.ts.
import { join } from "node:path";
import type { Command } from "commander";
import { archiveMission } from "../lib/autopilot/mission-archive.js";
import { resolveMission } from "../lib/autopilot/mission-resolver.js";
import { realLivenessProbe } from "../lib/autopilot/process-liveness-probe.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { log } from "../lib/terminal-logger.js";

export interface RegisterAutopilotArchiveCommandDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  machineLockPath: string;
}

export function registerAutopilotArchiveCommand(
  autopilot: Command,
  deps: RegisterAutopilotArchiveCommandDeps,
): void {
  autopilot
    .command("archive")
    .description("Chuyển mission ra khỏi .avatar/missions/ (gỡ worktree, giữ branch)")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--force", "Archive dù worktree còn thay đổi chưa commit của người dùng")
    .option("--json", "Output JSON")
    .action(async (opts: { mission?: string; repo?: string; force?: boolean; json?: boolean }) => {
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      const resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });
      const worktreePath = join(resolved.missionHome, "workspace");

      const result = await archiveMission({
        workspaceRoot,
        missionId: resolved.missionId,
        missionHome: resolved.missionHome,
        worktreePath,
        force: opts.force,
        lockPath: deps.machineLockPath,
        liveness: realLivenessProbe,
      });

      if (opts.json) {
        writeMachineReadableJson(
          result.ok
            ? {
                command: "autopilot archive",
                mission_id: result.mission_id,
                archived_to: result.archived_to,
                worktree_removed: result.worktree_removed,
                branch_kept: result.branch_kept,
                ...(result.head_ref !== undefined ? { head_ref: result.head_ref } : {}),
              }
            : {
                command: "autopilot archive",
                ok: false,
                reason: result.reason,
                message: result.message,
              },
        );
      } else if (result.ok) {
        log.info(`archive: mission ${result.mission_id} -> ${result.archived_to}`);
        log.info(`  worktree gỡ: ${result.worktree_removed ? "có" : "không (đã mất từ trước)"}`);
        log.info(
          `  commit cuối của mission giữ ở ${result.head_ref ?? "nhánh autopilot/<id>"} (không xoá).`,
        );
      } else {
        log.error(result.message);
      }

      if (!result.ok) process.exit(1);
    });
}
